import { useEffect, useState, type FormEvent } from 'react'
import { currentLocaleTag } from '../../lib/formatting'
import {
  Plus,
  KeyRound,
  Trash2,
  Pencil,
  Loader2,
  ShieldAlert,
  CircleCheck,
  CircleSlash,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import Modal from '../../components/ui/Modal'
import CopyableValue from '../../components/ui/CopyableValue'
import { type CreateUserResponse, type Role, type UserListItem } from '@jr2os/shared'
import { useAuth } from '../../contexts/AuthContext'
import { centsToEurosInput, formatCents, parseEurosToCents } from '../../lib/money'
import { useTableSort } from '../../lib/useTableSort'

const roleAccent: Record<Role, string> = {
  ADMIN: 'bg-jr2-500/15 text-jr2-700 dark:text-jr2-200 border-jr2-500/40',
  CONSULTOR: 'bg-purple-500/15 text-purple-700 dark:text-purple-200 border-purple-500/40',
  ANALISTA: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-500/40',
  LECTURA: 'bg-slate-500/15 t-secondary border-slate-500/40',
  CONTABILIDAD: 'bg-amber-500/15 text-amber-700 dark:text-amber-200 border-amber-500/40',
}

interface ProjectRoleOption {
  id: string
  name: string
  countsAsCAC: boolean
  active: boolean
}

interface UserCapacity {
  id: string
  projectRoleId: string
  projectRoleName: string
  countsAsCAC: boolean
  hourlyRateCents: number
  costPerHourCents: number
}

export default function Users() {
  const { t } = useTranslation('settings')
  const auth = useAuth()
  const currentUserId = auth.status === 'authenticated' ? auth.user.id : null

  const [users, setUsers] = useState<UserListItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [detailUser, setDetailUser] = useState<UserListItem | null>(null)
  const [showPasswordFor, setShowPasswordFor] = useState<{
    email: string
    tempPassword: string
    mustChange: boolean
  } | null>(null)
  // Reset de contraseña: modal con opción de NO forzar el cambio en el próximo
  // acceso (checkbox marcado por defecto = comportamiento histórico).
  const [resetFor, setResetFor] = useState<UserListItem | null>(null)
  const [resetMustChange, setResetMustChange] = useState(true)
  const [resetting, setResetting] = useState(false)

  // Orden por columna (en la URL: es una página entera). Sin orden elegido, el de siempre de la API.
  const { rows: sortedUsers, header } = useTableSort(users, {
    name: (u) => u.fullName,
    email: (u) => u.email,
    role: (u) => t('enums:Role.' + u.role),
    status: (u) => (u.isActive ? t('users.active') : t('users.inactive')),
    lastLogin: (u) => u.lastLoginAt,
  })

  const reload = async () => {
    setError(null)
    try {
      const { users } = await api.get<{ users: UserListItem[] }>('/users')
      setUsers(users)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('users.loadError'))
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const onResetPassword = (u: UserListItem) => {
    setResetMustChange(true)
    setResetFor(u)
  }

  const onConfirmReset = async () => {
    if (!resetFor) return
    setResetting(true)
    try {
      const email = resetFor.email
      const { tempPassword } = await api.post<{ tempPassword: string }>(
        `/users/${resetFor.id}/reset-password`,
        { mustChangePassword: resetMustChange }
      )
      setResetFor(null)
      setShowPasswordFor({ email, tempPassword, mustChange: resetMustChange })
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('users.genericError'))
    } finally {
      setResetting(false)
    }
  }

  const onDelete = async (u: UserListItem) => {
    if (
      !(await confirmDialog({
        message: t('users.confirmDelete', { name: u.fullName }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/users/${u.id}`)
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('users.genericError'))
    }
  }

  const onToggleActive = async (u: UserListItem) => {
    try {
      await api.patch(`/users/${u.id}`, { isActive: !u.isActive })
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('users.genericError'))
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">{t('users.title')}</h2>
          <p className="t-muted text-sm mt-1">{t('users.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 transition-all flex items-center gap-1.5"
        >
          <Plus size={16} /> {t('users.newButton')}
        </button>
      </header>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
              {header('name', t('users.table.name'), { className: 'px-4 py-3 font-medium' })}
              {header('email', t('users.table.email'), { className: 'px-4 py-3 font-medium' })}
              {header('role', t('users.table.role'), { className: 'px-4 py-3 font-medium' })}
              {header('status', t('users.table.status'), { className: 'px-4 py-3 font-medium' })}
              {header('lastLogin', t('users.table.lastLogin'), { className: 'px-4 py-3 font-medium' })}
              <th className="px-4 py-3 font-medium text-right">{t('users.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users === null ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center t-muted">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> {t('users.loading')}
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center t-muted">
                  {t('users.empty')}
                </td>
              </tr>
            ) : (
              sortedUsers.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => setDetailUser(u)}
                  className="border-b border-base/50 last:border-b-0 hover:bg-hover/50 cursor-pointer"
                >
                  <td className="px-4 py-3 t-primary font-medium">
                    {u.fullName}
                    {u.id === currentUserId && <span className="text-xs t-faint ml-2">{t('users.youSuffix')}</span>}
                    {u.mustChangePassword && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300">
                        <ShieldAlert size={12} /> {t('users.mustChangePassword')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 t-secondary font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${roleAccent[u.role]}`}
                    >
                      {t('enums:Role.' + u.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.isActive ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
                        <CircleCheck size={14} /> {t('users.active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs t-muted">
                        <CircleSlash size={14} /> {t('users.inactive')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 t-muted text-xs">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString(currentLocaleTag()) : '—'}
                  </td>
                  <td
                    className="px-4 py-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => onResetPassword(u)}
                        title={
                          u.id === currentUserId
                            ? t('users.actions.resetPasswordSelf')
                            : t('users.actions.resetPassword')
                        }
                        disabled={u.id === currentUserId}
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <KeyRound size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onToggleActive(u)}
                        title={u.isActive ? t('users.actions.deactivate') : t('users.actions.activate')}
                        disabled={u.id === currentUserId}
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-amber-700 dark:hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(u)}
                        title={t('users.actions.delete')}
                        disabled={u.id === currentUserId}
                        className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CreateUserModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(resp) => {
          setShowCreate(false)
          setShowPasswordFor({
            email: resp.user.email,
            tempPassword: resp.tempPassword,
            mustChange: true,
          })
          void reload()
        }}
      />

      <UserDetailModal
        key={detailUser?.id ?? 'none'} // cada ficha empieza con su tabla de capacidades sin ordenar
        user={detailUser}
        onClose={() => setDetailUser(null)}
      />

      <Modal
        open={resetFor !== null}
        onClose={() => {
          if (!resetting) setResetFor(null)
        }}
        title={t('users.resetModal.title')}
      >
        {resetFor && (
          <div className="space-y-4">
            <p className="text-sm t-secondary">
              {t('users.resetModal.description', { name: resetFor.fullName })}
            </p>
            <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg border border-base p-3 hover:bg-hover/40">
              <input
                type="checkbox"
                checked={resetMustChange}
                onChange={(e) => setResetMustChange(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-[var(--border)] accent-jr2-600"
              />
              <span>
                <span className="block text-sm t-primary font-medium">
                  {t('users.resetModal.forceChange')}
                </span>
                <span className="block text-xs t-muted mt-0.5">
                  {t('users.resetModal.forceChangeHelp')}
                </span>
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setResetFor(null)}
                disabled={resetting}
                className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)] disabled:opacity-50"
              >
                {t('users.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void onConfirmReset()}
                disabled={resetting}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
              >
                {resetting && <Loader2 size={14} className="animate-spin" />}
                {t('users.resetModal.confirm')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={showPasswordFor !== null}
        onClose={() => setShowPasswordFor(null)}
        title={t('users.tempPassword.title')}
        description={t('users.tempPassword.description')}
      >
        {showPasswordFor && (
          <div className="space-y-4">
            <CopyableValue label={t('users.tempPassword.emailLabel')} value={showPasswordFor.email} />
            <CopyableValue label={t('users.tempPassword.passwordLabel')} value={showPasswordFor.tempPassword} />
            <p className="text-xs t-muted">
              {showPasswordFor.mustChange
                ? t('users.tempPassword.note')
                : t('users.tempPassword.noteNoChange')}
            </p>
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowPasswordFor(null)}
                className="px-4 py-2 rounded-lg text-sm bg-jr2-500/15 text-jr2-700 dark:text-jr2-200 hover:bg-jr2-500/25"
              >
                {t('users.tempPassword.copied')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

interface CreateProps {
  open: boolean
  onClose: () => void
  onCreated: (resp: CreateUserResponse) => void
}

function CreateUserModal({ open, onClose, onCreated }: CreateProps) {
  const { t } = useTranslation('settings')
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<Role>('CONSULTOR')
  const [certifications, setCertifications] = useState('')
  const [costEuros, setCostEuros] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setEmail('')
    setFullName('')
    setRole('CONSULTOR')
    setCertifications('')
    setCostEuros('')
    setError(null)
  }

  const close = () => {
    reset()
    onClose()
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const payload = {
        email,
        fullName,
        role,
        ...(costEuros.trim() ? { costPerHourCents: parseEurosToCents(costEuros) } : {}),
        ...(role === 'CONSULTOR'
          ? {
              consultantProfile: {
                certifications: certifications
                  .split(',')
                  .map((c) => c.trim())
                  .filter(Boolean),
              },
            }
          : {}),
      }
      const resp = await api.post<CreateUserResponse>('/users', payload)
      reset()
      onCreated(resp)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(t('users.networkError'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onClose={close} title={t('users.createModal.title')} maxWidth="max-w-xl">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('users.createModal.fullName')}</label>
            <input
              required
              minLength={2}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60"
            />
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('users.createModal.email')}</label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm t-secondary mb-1.5">{t('users.createModal.role')}</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60"
          >
            <option value="ADMIN">{t('users.roles.admin')}</option>
            <option value="CONSULTOR">{t('users.roles.consultor')}</option>
            <option value="ANALISTA">{t('users.roles.analista')}</option>
            <option value="LECTURA">{t('users.roles.lectura')}</option>
            <option value="CONTABILIDAD">{t('users.roles.contabilidad')}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm t-secondary mb-1.5">{t('users.createModal.costLabel')}</label>
          <input
            value={costEuros}
            onChange={(e) => setCostEuros(e.target.value)}
            placeholder="0,00"
            inputMode="decimal"
            className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary font-mono focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60"
          />
          <p className="text-[11px] t-faint mt-1">{t('users.createModal.costHelp')}</p>
        </div>

        {role === 'CONSULTOR' && (
          <div className="border-t border-base pt-4">
            <label className="block text-sm t-secondary mb-1.5">{t('users.createModal.certifications')}</label>
            <input
              value={certifications}
              onChange={(e) => setCertifications(e.target.value)}
              placeholder={t('users.createModal.certificationsPlaceholder')}
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary"
            />
            <p className="text-[11px] t-faint mt-1">
              {t('users.createModal.certificationsHelp')}
            </p>
          </div>
        )}

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={close}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('users.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !email || !fullName}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {t('users.createModal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// =====================================================================
// UserDetailModal — ficha del usuario con sección "Capacidades"
// =====================================================================

interface UserDetailProps {
  user: UserListItem | null
  onClose: () => void
}

function UserDetailModal({ user, onClose }: UserDetailProps) {
  const { t } = useTranslation('settings')
  const [capacities, setCapacities] = useState<UserCapacity[] | null>(null)
  const [allRoles, setAllRoles] = useState<ProjectRoleOption[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [addingCapacity, setAddingCapacity] = useState(false)
  const [editingCapacity, setEditingCapacity] = useState<UserCapacity | null>(null)
  // F45 — Coste interno por hora de la persona (default del coste al registrar tiempo).
  const [costEuros, setCostEuros] = useState('')
  const [savingCost, setSavingCost] = useState(false)
  const [exemptAllowances, setExemptAllowances] = useState(false)
  const [savingExempt, setSavingExempt] = useState(false)

  // Orden de la tabla de capacidades: solo en memoria (es un modal). ☠️ El hook va ANTES del `if (!user) return null`.
  const { rows: sortedCapacities, header: capHeader } = useTableSort(
    capacities,
    {
      projectRole: (c) => c.projectRoleName,
      rate: (c) => c.hourlyRateCents,
      cost: (c) => c.costPerHourCents,
    },
    { persist: 'state' }
  )

  const reload = async (userId: string) => {
    setError(null)
    try {
      const [{ items }, { items: roles }] = await Promise.all([
        api.get<{ items: UserCapacity[] }>(`/users/${userId}/project-roles`),
        api.get<{ items: ProjectRoleOption[] }>('/project-roles'),
      ])
      setCapacities(items)
      setAllRoles(roles)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(err instanceof Error ? err.message : t('users.detail.loadError'))
      }
    }
  }

  useEffect(() => {
    if (!user) {
      setCapacities(null)
      setAllRoles(null)
      setError(null)
      setAddingCapacity(false)
      setEditingCapacity(null)
      setCostEuros('')
      setExemptAllowances(false)
      return
    }
    setCostEuros(user.costPerHourCents != null ? centsToEurosInput(user.costPerHourCents) : '')
    setExemptAllowances(user.receivesExemptAllowances)
    void reload(user.id)
  }, [user])

  const onSaveCost = async () => {
    if (!user) return
    setSavingCost(true)
    setError(null)
    try {
      await api.patch(`/users/${user.id}`, {
        costPerHourCents: costEuros.trim() ? parseEurosToCents(costEuros) : null,
      })
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(t('users.networkError'))
      }
    } finally {
      setSavingCost(false)
    }
  }

  const onSaveExemptAllowances = async (next: boolean) => {
    if (!user) return
    setExemptAllowances(next)
    setSavingExempt(true)
    setError(null)
    try {
      await api.patch(`/users/${user.id}`, { receivesExemptAllowances: next })
    } catch (err) {
      // Si el servidor no lo acepta, la casilla vuelve a donde estaba: una
      // casilla que miente sobre lo que hay en la BD es peor que un error.
      setExemptAllowances(!next)
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(t('users.networkError'))
      }
    } finally {
      setSavingExempt(false)
    }
  }

  const onDeleteCapacity = async (cap: UserCapacity) => {
    if (!user) return
    if (
      !(await confirmDialog({
        message: t('users.detail.confirmRemoveCapacity', {
          role: cap.projectRoleName,
          name: user.fullName,
        }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/users/${user.id}/project-roles/${cap.id}`)
      await reload(user.id)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        toast.error(body?.error ?? err.message)
      } else {
        toast.error(t('users.networkError'))
      }
    }
  }

  if (!user) return null

  const usedRoleIds = new Set((capacities ?? []).map((c) => c.projectRoleId))
  const availableRoles = (allRoles ?? []).filter(
    (r) => r.active && !usedRoleIds.has(r.id)
  )

  return (
    <>
      <Modal
        open={user !== null && editingCapacity === null && !addingCapacity}
        onClose={onClose}
        title={user.fullName}
        description={user.email}
        maxWidth="max-w-2xl"
      >
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-xs t-faint uppercase tracking-wider mb-1">{t('users.detail.systemRole')}</div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full border ${roleAccent[user.role]}`}
              >
                {t('enums:Role.' + user.role)}
              </span>
            </div>
            <div>
              <div className="text-xs t-faint uppercase tracking-wider mb-1">{t('users.detail.status')}</div>
              {user.isActive ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
                  <CircleCheck size={14} /> {t('users.active')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs t-muted">
                  <CircleSlash size={14} /> {t('users.inactive')}
                </span>
              )}
            </div>
            <div>
              <div className="text-xs t-faint uppercase tracking-wider mb-1">{t('users.table.lastLogin')}</div>
              <div className="t-secondary text-xs">
                {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString(currentLocaleTag()) : '—'}
              </div>
            </div>
          </div>

          {error && (
            <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30">
              {error}
            </div>
          )}

          <section className="border-t border-base pt-4">
            <h3 className="text-sm font-semibold t-primary">{t('users.detail.costLabel')}</h3>
            <p className="text-xs t-muted mt-0.5 mb-2">{t('users.detail.costHelp')}</p>
            <div className="flex items-center gap-2">
              <input
                value={costEuros}
                onChange={(e) => setCostEuros(e.target.value)}
                placeholder="0,00"
                inputMode="decimal"
                className="w-32 px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary font-mono focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60"
              />
              <span className="text-sm t-muted">€/h</span>
              <button
                type="button"
                onClick={() => void onSaveCost()}
                disabled={savingCost}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {savingCost && <Loader2 size={14} className="animate-spin" />}
                {t('users.detail.costSave')}
              </button>
            </div>
          </section>

          <section className="border-t border-base pt-4">
            <h3 className="text-sm font-semibold t-primary">{t('users.detail.allowancesLabel')}</h3>
            <p className="text-xs t-muted mt-0.5 mb-2">{t('users.detail.allowancesHelp')}</p>
            <label className="flex items-start gap-2 text-sm t-secondary">
              <input
                type="checkbox"
                checked={exemptAllowances}
                disabled={savingExempt}
                onChange={(e) => void onSaveExemptAllowances(e.target.checked)}
                className="mt-0.5"
              />
              <span>{t('users.detail.allowancesToggle')}</span>
            </label>
          </section>

          <section className="border-t border-base pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold t-primary">{t('users.detail.capacities')}</h3>
                <p className="text-xs t-muted mt-0.5">
                  {t('users.detail.capacitiesHelp')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAddingCapacity(true)}
                disabled={availableRoles.length === 0 || allRoles === null}
                title={
                  availableRoles.length === 0
                    ? t('users.detail.noRolesAvailable')
                    : t('users.detail.addCapacity')
                }
                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <Plus size={14} /> {t('users.detail.addCapacity')}
              </button>
            </div>

            {capacities === null ? (
              <div className="text-center py-6 t-muted text-sm">
                <Loader2 size={16} className="inline animate-spin mr-2" /> {t('users.loading')}
              </div>
            ) : capacities.length === 0 ? (
              <div className="text-center py-6 t-muted text-sm border border-dashed border-base rounded-lg">
                {t('users.detail.emptyCapacities')}
              </div>
            ) : (
              <div className="surface overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
                      {capHeader('projectRole', t('users.detail.projectRole'), { className: 'px-3 py-2 font-medium' })}
                      {capHeader('rate', t('users.detail.rate'), { align: 'right', className: 'px-3 py-2 font-medium' })}
                      {capHeader('cost', t('users.detail.cost'), { align: 'right', className: 'px-3 py-2 font-medium' })}
                      <th className="px-3 py-2 font-medium text-right">{t('users.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCapacities.map((c) => (
                      <tr
                        key={c.id}
                        className="border-b border-base/50 last:border-b-0 hover:bg-hover/40"
                      >
                        <td className="px-3 py-2 t-primary">
                          {c.projectRoleName}
                          {c.countsAsCAC && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-200">
                              CAC
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs t-secondary">
                          {formatCents(c.hourlyRateCents)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs t-secondary">
                          {formatCents(c.costPerHourCents)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setEditingCapacity(c)}
                              title={t('users.detail.edit')}
                              className="w-7 h-7 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => onDeleteCapacity(c)}
                              title={t('users.detail.removeCapacity')}
                              className="w-7 h-7 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
            >
              {t('users.close')}
            </button>
          </div>
        </div>
      </Modal>

      <CapacityFormModal
        open={addingCapacity}
        mode="create"
        userId={user.id}
        availableRoles={availableRoles}
        onClose={() => setAddingCapacity(false)}
        onSaved={async () => {
          setAddingCapacity(false)
          await reload(user.id)
        }}
      />

      <CapacityFormModal
        open={editingCapacity !== null}
        mode="edit"
        userId={user.id}
        capacity={editingCapacity}
        onClose={() => setEditingCapacity(null)}
        onSaved={async () => {
          setEditingCapacity(null)
          await reload(user.id)
        }}
      />
    </>
  )
}

// =====================================================================
// CapacityFormModal — alta/edición de una capacidad
// =====================================================================

interface CapacityFormProps {
  open: boolean
  mode: 'create' | 'edit'
  userId: string
  capacity?: UserCapacity | null
  availableRoles?: ProjectRoleOption[]
  onClose: () => void
  onSaved: () => void | Promise<void>
}

function CapacityFormModal({
  open,
  mode,
  userId,
  capacity,
  availableRoles,
  onClose,
  onSaved,
}: CapacityFormProps) {
  const { t } = useTranslation('settings')
  const [projectRoleId, setProjectRoleId] = useState('')
  const [hourlyRateEuros, setHourlyRateEuros] = useState('')
  const [costPerHourEuros, setCostPerHourEuros] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && capacity) {
      setProjectRoleId(capacity.projectRoleId)
      setHourlyRateEuros(centsToEurosInput(capacity.hourlyRateCents))
      setCostPerHourEuros(centsToEurosInput(capacity.costPerHourCents))
    } else {
      setProjectRoleId(availableRoles && availableRoles[0] ? availableRoles[0].id : '')
      setHourlyRateEuros('')
      setCostPerHourEuros('')
    }
    setError(null)
  }, [open, mode, capacity, availableRoles])

  if (!open) return null

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const hourlyRateCents = parseEurosToCents(hourlyRateEuros)
      const costPerHourCents = parseEurosToCents(costPerHourEuros)
      if (mode === 'create') {
        if (!projectRoleId) {
          setError(t('users.capacity.selectRole'))
          setSubmitting(false)
          return
        }
        await api.post(`/users/${userId}/project-roles`, {
          projectRoleId,
          hourlyRateCents,
          costPerHourCents,
        })
      } else if (capacity) {
        await api.patch(`/users/${userId}/project-roles/${capacity.id}`, {
          hourlyRateCents,
          costPerHourCents,
        })
      }
      await onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(t('users.networkError'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? t('users.capacity.addTitle') : t('users.capacity.editTitle')}
      maxWidth="max-w-md"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm t-secondary mb-1.5">{t('users.detail.projectRole')}</label>
          {mode === 'create' ? (
            <select
              required
              value={projectRoleId}
              onChange={(e) => setProjectRoleId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60"
            >
              {(availableRoles ?? []).length === 0 ? (
                <option value="">{t('users.capacity.noRolesLeft')}</option>
              ) : (
                (availableRoles ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.countsAsCAC ? ' · CAC' : ''}
                  </option>
                ))
              )}
            </select>
          ) : (
            <div className="px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-secondary text-sm">
              {capacity?.projectRoleName ?? '—'}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('users.detail.rate')}</label>
            <input
              value={hourlyRateEuros}
              onChange={(e) => setHourlyRateEuros(e.target.value)}
              placeholder="120,00"
              inputMode="decimal"
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary font-mono focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60"
            />
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('users.detail.cost')}</label>
            <input
              value={costPerHourEuros}
              onChange={(e) => setCostPerHourEuros(e.target.value)}
              placeholder="40,00"
              inputMode="decimal"
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary font-mono focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60"
            />
          </div>
        </div>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('users.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || (mode === 'create' && !projectRoleId)}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {mode === 'create' ? t('users.capacity.add') : t('users.capacity.save')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
